import { WeatherMapLayers } from '@sofarocean/atlas/types';
import { LngLatLike } from 'mapbox-gl';
import { ReactNode, createContext, useContext, useMemo, useState } from 'react';

import { SpotterInspectorProps } from '@/components/map/inspectors/SpotterInspector';
import { equalsShallow } from '@/utils/equalityHelpers';

export type SpotterMapLayers = {
    mySpotters?: boolean;
    gridSpotters?: boolean;
    sharedSpotters?: boolean;
};

export type MapInspectorProps =
    | {
          type: 'weather';
          position: LngLatLike;
      }
    | ({
          type: 'spotter';
          position: LngLatLike;
      } & SpotterInspectorProps);

export type MapBaseLayer = 'satellite' | 'terrain';

export type CameraOptions = { zoom: number; center: LngLatLike };

export const SPOTTER_LIST_FILTERS = ['all', 'active'] as const;

export type SpotterListFilter = (typeof SPOTTER_LIST_FILTERS)[number];

export type SpotterListSort = 'lastUpdate' | 'name' | 'proximity';

export type SpotterListOptions = { filter: SpotterListFilter; sort: SpotterListSort };

export type MapUiContextType = {
    weatherLayers: WeatherMapLayers;
    setWeatherLayer<T extends keyof WeatherMapLayers>(key: T, value: WeatherMapLayers[T]): void;
    spotterLayers: SpotterMapLayers;
    setSpotterLayer<T extends keyof SpotterMapLayers>(key: T, value: SpotterMapLayers[T]): void;
    mapInspector?: MapInspectorProps;
    setMapInspector: React.Dispatch<React.SetStateAction<MapInspectorProps | undefined>>;
    weatherModel?: string;
    setWeatherModel(value?: string): void;
    showMyLocation?: boolean;
    setShowMyLocation(value?: boolean): void;
    cameraOptions?: CameraOptions;
    setCameraOptions(value?: CameraOptions): void;
    baseLayer: MapBaseLayer;
    setBaseLayer(value: MapBaseLayer): void;
    spotterListOptions: SpotterListOptions;
    setSpotterListOptions(value: Partial<SpotterListOptions>): void;

    spotterIdsInViewport: readonly string[];
    setSpotterIdsInViewport(spotterIds: string[]): void;
};

const defaultMapUiContextData: MapUiContextType = {
    weatherLayers: {},
    setWeatherLayer: () => {},
    spotterLayers: {},
    setSpotterLayer: () => {},
    mapInspector: undefined,
    setMapInspector: () => {},
    weatherModel: undefined,
    setWeatherModel: () => {},
    showMyLocation: undefined,
    setShowMyLocation: () => {},
    cameraOptions: undefined,
    setCameraOptions: () => {},
    baseLayer: 'terrain',
    setBaseLayer: () => {},
    spotterListOptions: { sort: 'lastUpdate', filter: 'all' },
    setSpotterListOptions: () => {},

    spotterIdsInViewport: [] as const,
    setSpotterIdsInViewport: () => {},
} as const;

const MapUiContext = createContext(defaultMapUiContextData);

const MapUiContextProvider = ({ children }: { children: ReactNode }) => {
    const [weatherLayers, setWeatherLayers] = useState<WeatherMapLayers>({
        raster: 'combinedWaves',
        showIsobars: false,
        showPressureCenters: false,
    });

    const [spotterLayers, setSpotterLayers] = useState<SpotterMapLayers>({
        mySpotters: true,
        gridSpotters: false,
        sharedSpotters: false,
    });

    const [mapInspector, setMapInspector] = useState<MapInspectorProps>();

    const [weatherModel, setWeatherModel] = useState<string>();
    const [showMyLocation, setShowMyLocation] = useState<boolean>();
    const [cameraOptions, setCameraOptions] = useState<CameraOptions>();
    const [baseLayer, setBaseLayer] = useState<MapBaseLayer>('terrain');
    const [spotterListOptions, setSpotterListOptions] = useState(defaultMapUiContextData.spotterListOptions);
    const [spotterIdsInViewport, setSpotterIdsInViewportValue] = useState<string[]>([]);

    let honoraryViewportSpotter: string | null = null;
    if (mapInspector?.type === 'spotter') {
        honoraryViewportSpotter = mapInspector.spotterId;
    }

    /*
     * Memoized on its own so that the identity survives a pan. Every other value the context is built from
     * changes on a map move, and a consumer keyed on this array cannot tell that apart from the viewport
     * naming a different set of spotters.
     */
    const spotterIdsInViewportWithHonorary = useMemo(
        () =>
            !honoraryViewportSpotter || spotterIdsInViewport.includes(honoraryViewportSpotter)
                ? spotterIdsInViewport
                : [...spotterIdsInViewport, honoraryViewportSpotter],
        [spotterIdsInViewport, honoraryViewportSpotter]
    );

    const contextValue: MapUiContextType = useMemo(
        () => ({
            weatherLayers,
            setWeatherLayer(key, value) {
                setWeatherLayers({ ...weatherLayers, [key]: value });
            },
            spotterLayers,
            setSpotterLayer(key, value) {
                setSpotterLayers({ ...spotterLayers, [key]: value });
            },
            mapInspector,
            setMapInspector,
            weatherModel,
            setWeatherModel,
            showMyLocation,
            setShowMyLocation,
            cameraOptions,
            setCameraOptions,
            baseLayer,
            setBaseLayer,
            spotterListOptions,
            setSpotterListOptions: (options) => setSpotterListOptions((oldValue) => ({ ...oldValue, ...options })),
            spotterIdsInViewport: spotterIdsInViewportWithHonorary,
            /*
             * A pan recomputes this from the map bounds, which mostly answers with the same spotters. The
             * identity is what every query and derivation downstream keys on, so only a changed membership
             * is allowed to replace it.
             */
            setSpotterIdsInViewport: (spotterIds) =>
                setSpotterIdsInViewportValue((held) => (equalsShallow(held, spotterIds) ? held : spotterIds)),
        }),
        [
            weatherLayers,
            spotterLayers,
            mapInspector,
            weatherModel,
            showMyLocation,
            cameraOptions,
            baseLayer,
            spotterListOptions,
            spotterIdsInViewportWithHonorary,
        ]
    );

    return <MapUiContext.Provider value={contextValue}>{children}</MapUiContext.Provider>;
};

/**
 * Variant of the map ui context provider specific to the /public map, which has some slightly different mechanics:
 * - Independent Spotter filter defaults to "Active Spotters"
 *
 * Otherwise, delegates to the containing MapUiContextProvider
 */
export const PublicMapUiContextProvider = ({ children }: { children: ReactNode }) => {
    const parent = useContext(MapUiContext);
    const [spotterListOptions, setSpotterListOptions] = useState<SpotterListOptions>({
        ...parent.spotterListOptions,
        filter: 'active',
    });

    const contextValue: MapUiContextType = {
        ...parent,
        spotterListOptions,
        setSpotterListOptions: (options) => setSpotterListOptions((oldValue) => ({ ...oldValue, ...options })),
    };

    return <MapUiContext.Provider value={contextValue}>{children}</MapUiContext.Provider>;
};

const useMapUiContext = () => useContext(MapUiContext);

export { MapUiContextProvider, useMapUiContext };
